import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { FaFacebook, FaInstagram } from 'react-icons/fa';

const NAV_LINKS = [
  { label: 'TRIVIA SCHEDULE',    href: '#schedule' },
  { label: 'BOOK LOCATION OR EVENT', href: '#contact'  },
];

function HamburgerIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path fillRule="evenodd" d="M3 6.75A.75.75 0 013.75 6h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 6.75zm0 5.25a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 12zm0 5.25a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75a.75.75 0 01-.75-.75z" clipRule="evenodd" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
      <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function Header({ hideNavLinks = false }) {
  const [menuOpen, setMenuOpen]   = useState(false);
  const [facebookUrl, setFacebook] = useState(null);
  const [instagramUrl, setInstagram] = useState(null);

  useEffect(() => {
    async function fetchSocialLinks() {
      const { data } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['facebook_url', 'instagram_url']);

      (data || []).forEach(row => {
        if (row.key === 'facebook_url')  setFacebook(row.value  || null);
        if (row.key === 'instagram_url') setInstagram(row.value || null);
      });
    }
    fetchSocialLinks();
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (!element) return;
    const headerHeight = document.querySelector('header')?.offsetHeight ?? 120;
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerHeight;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };

  const SocialIcons = () => (
    <div className="flex items-center gap-4">
      {facebookUrl && (
        <a href={facebookUrl} target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">
          <FaFacebook size={20} color="#F7941D" aria-label="Facebook" />
        </a>
      )}
      {instagramUrl && (
        <a href={instagramUrl} target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">
          <FaInstagram size={24} color="#F7941D" aria-label="Instagram" />
        </a>
      )}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 shadow-lg" style={{ backgroundColor: '#221F1F' }}>
      <div className="flex items-center justify-between px-6 py-8">
        {/* Left spacer — mirrors right nav width to keep logo centered */}
        <div className="hidden xl:flex flex-1" />

        {/* Mobile balance spacer — mirrors hamburger width to keep logo centered */}
        <div className="w-6 h-6 xl:hidden flex-shrink-0" aria-hidden="true" />

        {/* Logo — mobile */}
        <a href="/" className="flex-1 flex justify-center xl:hidden">
          <img
            src="/Assets/Logos/TNL-Horizontal-White&Grey-NoSubheader.png"
            alt="Trivia Nights Live"
            className="object-contain"
            style={{ height: 'clamp(2rem, 5vw, 4rem)', maxWidth: '220px' }}
          />
        </a>

        {/* Logo — desktop (stays in same tab) */}
        <a href="/" className="flex-shrink-0 hidden xl:block">
          <img
            src="/Assets/Logos/TNL-Horizontal-White&Grey.png"
            alt="Trivia Nights Live"
            style={{ height: 'clamp(2.5rem, 5vw, 5rem)', width: 'auto', maxWidth: '30vw', minWidth: '120px' }}
          />
        </a>

        {/* Desktop nav — fluid, scales with viewport */}
        <nav
          className="hidden xl:flex items-center flex-1 justify-end"
          style={{ gap: 'clamp(0.75rem, 2vw, 2.5rem)' }}
        >
          {!hideNavLinks && NAV_LINKS.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              onClick={scrollTo(href.replace('#', ''))}
              className="font-poppins font-semibold tracking-widest text-white hover:text-gold transition-colors duration-200 flex-shrink-0 whitespace-nowrap"
              style={{ fontSize: 'clamp(0.6rem, 1.1vw, 0.875rem)' }}
            >
              {label}
            </a>
          ))}
          <div className="flex-shrink-0">
            <SocialIcons />
          </div>
        </nav>

        {/* Mobile hamburger — below 768px only */}
        <button
          className="xl:hidden text-gold flex-shrink-0"
          onClick={() => setMenuOpen(prev => !prev)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <nav className="xl:hidden bg-dark border-t border-gray-700 px-6 py-6 flex flex-col items-center gap-6">
          {!hideNavLinks && NAV_LINKS.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              onClick={scrollTo(href.replace('#', ''))}
              className="font-poppins text-sm font-semibold tracking-widest text-white hover:text-gold transition-colors duration-200 text-center"
            >
              {label}
            </a>
          ))}
          <div className="pt-2">
            <SocialIcons />
          </div>
        </nav>
      )}
    </header>
  );
}

export default Header;
